/**
 * Per-item gift wrapping — added via Cowork.
 *
 * Model
 * -----
 * Ticking a cart line sets a PUBLIC line item property on that exact line (default
 * `Presentinslagning: Ja`). That property is what tells the merchant, on the order, which items
 * are wrapped. Separately, exactly ONE wrapping fee line is kept in the cart whose quantity
 * equals the total number of wrapped UNITS — a line of three vases ticked for wrapping is three
 * wrapping jobs, so it costs three fees. `reconcile()` owns that arithmetic and is the only place
 * the fee line is ever touched.
 *
 * Three Shopify facts shape this file, all from the Ajax Cart API docs:
 *
 *   1. "Whenever a POST request includes properties, it overwrites the entire properties object."
 *      Every write therefore re-reads the line's current properties and sends the full merged set.
 *
 *   2. "It's not possible to set a line item's properties property to an empty object once a
 *      value is set. If you need to remove all line item properties, then you need to remove the
 *      entire line item." Unticking must therefore DROP the key, never blank it: the admin order
 *      page shows a blank-valued property as its bare name ("Presentinslagning"), which reads as
 *      wrapped (STO-996). `unwrapLine()` re-sends the line's other properties without the key or,
 *      when it was the only one, removes the line and adds it back clean.
 *
 *   3. A line key is not stable — it is reminted whenever that line's properties change. Nothing
 *      here trusts a key across a mutation; every write re-reads the cart first.
 *
 * One theme fact matters just as much: <line-item> in theme.js delegates `change` on
 * `[data-line-key]` and feeds `parseInt(target.value)` straight into a quantity change. Our
 * controls therefore use `data-gift-wrap-key`, never `data-line-key`, and stop their own change
 * events from bubbling. Using the obvious attribute name here deletes the customer's line item.
 *
 * Refresh strategy matches what the theme does natively:
 *   - drawer -> dispatch `cart:refresh`; <cart-drawer> re-fetches its section and swaps content.
 *   - /cart  -> window.location.reload(), which is what the theme itself does after any change.
 *
 * Deliberately standalone: no imports from theme.js, so that bundle stays stock.
 */
(() => {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const RELOAD_GUARD = 'giftWrapReconciledOnce';

  const configElement = document.getElementById('gift-wrap-config');
  if (!configElement) return;

  let CONFIG;
  try {
    CONFIG = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }
  if (!CONFIG || !CONFIG.variantId || !CONFIG.productId) return;

  /* ------------------------------------------------------------------ requests */

  const request = async (url, options) => {
    const response = await fetch(url, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      ...options
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(payload.description || payload.message || `Cart request failed (${response.status})`);
    }

    return payload;
  };

  const postJson = (url, body) =>
    request(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body),
      // The theme uses keepalive for its own fire-and-forget cart-note save. Without it, a write
      // triggered by blurring into the Checkout button is cancelled by the navigation.
      keepalive: true
    });

  const getCart = () => request(`${ROOT}cart.js`, {});

  /* ------------------------------------------------- serialising + checkout lock */

  let chain = Promise.resolve();
  let pending = 0;

  /**
   * Hold the Checkout button while the cart is mid-adjustment. Without this a customer can bump a
   * wrapped line from 1 to 3 and reach checkout before the fee quantity catches up — paying for
   * one wrap on three items.
   */
  const setPending = (delta) => {
    pending = Math.max(0, pending + delta);
    const busy = pending > 0;

    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    });
  };

  /**
   * Every cart mutation goes through one queue. Two ticks in quick succession would otherwise both
   * read the cart, both compute a fee quantity from the same stale snapshot, and the second would
   * clobber the first.
   */
  const serial = (task) => {
    setPending(1);
    const run = chain.then(task, task);
    chain = run.then(() => setPending(-1), () => setPending(-1));
    return run;
  };

  /* ---------------------------------------------------------------- reconciler */

  // Matched on product_id, the same field the Liquid uses. Matching on variant_id here would
  // silently disagree with the theme the day the wrapping product gains a second variant.
  const isFeeLine = (item) => String(item.product_id) === String(CONFIG.productId);

  const isWrapped = (item) =>
    Boolean(item.properties) && item.properties[CONFIG.propertyKey] === CONFIG.propertyValue;

  /**
   * The wrapping fee line is hidden from the cart, so it must not inflate the header count either.
   * The theme's <cart-count> writes cart.item_count straight from the server, so we correct it
   * after each render rather than trying to beat it to the punch.
   */
  let visibleCount = null;

  const applyVisibleCartCount = () => {
    if (visibleCount === null) return;

    document.querySelectorAll('cart-count').forEach((element) => {
      const span = element.querySelector('span');
      if (!span) return;

      if (span.innerText !== String(visibleCount)) span.innerText = String(visibleCount);
      element.hidden = visibleCount === 0;
    });
  };

  /**
   * Bring the wrapping fee line in line with what is actually ticked.
   * Returns true when it changed the cart, so callers know whether a re-render is warranted.
   * `knownCart` lets a caller that has just read an unchanged cart skip a second request.
   */
  const reconcile = async (knownCart) => {
    const cart = knownCart || (await getCart());

    let wantedQuantity = 0;
    const feeLines = [];

    for (const item of cart.items) {
      if (isFeeLine(item)) {
        feeLines.push(item);
      } else if (isWrapped(item)) {
        wantedQuantity += item.quantity;
      }
    }

    const currentQuantity = feeLines.reduce((total, line) => total + line.quantity, 0);

    visibleCount = Math.max(0, cart.item_count - currentQuantity);
    applyVisibleCartCount();

    if (currentQuantity === wantedQuantity && feeLines.length <= 1) return false;

    let changed = false;

    // Collapse duplicate fee lines first. Removing a line does not invalidate the other keys.
    for (let index = feeLines.length - 1; index >= 1; index -= 1) {
      await postJson(`${ROOT}cart/change.js`, { id: feeLines[index].key, quantity: 0 });
      changed = true;
    }

    const feeLine = feeLines[0];

    if (wantedQuantity === 0) {
      if (feeLine) {
        await postJson(`${ROOT}cart/change.js`, { id: feeLine.key, quantity: 0 });
        changed = true;
      }
    } else if (feeLine) {
      if (feeLine.quantity !== wantedQuantity) {
        await postJson(`${ROOT}cart/change.js`, { id: feeLine.key, quantity: wantedQuantity });
        changed = true;
      }
    } else {
      await postJson(`${ROOT}cart/add.js`, {
        items: [{ id: Number(CONFIG.variantId), quantity: wantedQuantity }]
      });
      changed = true;
    }

    return changed;
  };

  /* ------------------------------------------ taking the wrapping key off a line */

  const hasWrapKey = (item, propertyKey) =>
    Boolean(item.properties) && Object.prototype.hasOwnProperty.call(item.properties, propertyKey);

  /**
   * Take the wrapping property off a line completely (STO-996).
   *
   * A blank value is not enough: the order then carries `Presentinslagning` with no value, and the
   * admin shows that bare name as if the item were wrapped. A POST with `properties` replaces the
   * whole object, so the key is dropped by re-sending the line's other properties without it. When
   * it was the only property that is impossible (Shopify refuses an empty object), so the line is
   * removed and added back with no properties, which also lets it merge with an untouched line of
   * the same variant.
   *
   * Remove first, then add: adding first would briefly need stock for both lines and fail on the
   * last units. If the add fails anyway (sold out meanwhile) the error reaches the caller, whose
   * reconcile() takes the fee away with the line.
   *
   * Side effects: the clean line can change position in the cart, and Shopify reports the
   * remove + add to the analytics pixels like any other cart change.
   */
  const unwrapLine = async (line, propertyKey) => {
    if (!hasWrapKey(line, propertyKey)) return;

    const remaining = Object.assign({}, line.properties);
    delete remaining[propertyKey];

    if (Object.keys(remaining).length > 0) {
      await postJson(`${ROOT}cart/change.js`, {
        id: line.key,
        quantity: line.quantity,
        properties: remaining
      });
      return;
    }

    await postJson(`${ROOT}cart/change.js`, { id: line.key, quantity: 0 });

    const item = { id: Number(line.variant_id), quantity: line.quantity };
    const plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    if (plan && plan.id) item.selling_plan = plan.id;

    await postJson(`${ROOT}cart/add.js`, { items: [item] });
  };

  // A line unticked before STO-996 still holds the key with an empty value.
  const hasBlankWrapKey = (item) => {
    if (isFeeLine(item) || !hasWrapKey(item, CONFIG.propertyKey)) return false;

    const value = item.properties[CONFIG.propertyKey];
    return value === null || String(value).trim() === '';
  };

  /**
   * Clean lines left blank by the old untick, so that bare name never reaches another order.
   * One read covers every line: removing a line leaves the other keys alone, and a clean line can
   * only merge into a line that carries no wrapping key, which is never one of these.
   * The snapshot is handed on to reconcile() only while it is still true.
   */
  const unwrapBlankLines = async () => {
    const cart = await getCart();
    const blanks = cart.items.filter(hasBlankWrapKey);

    for (const line of blanks) {
      await unwrapLine(line, CONFIG.propertyKey);
    }

    return { changed: blanks.length > 0, cart: blanks.length > 0 ? null : cart };
  };

  /* -------------------------------------------------------------------- shared */

  const onCartPage = () => {
    const themePageType =
      window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType;

    if (themePageType) return themePageType === 'cart';

    return window.location.pathname.replace(/\/$/, '').endsWith('/cart');
  };

  const refreshCart = (context) => {
    if (context === 'cart') {
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };

  class GiftWrapBase extends HTMLElement {
    // NOT `data-line-key` — see the note at the top of this file.
    get lineKey() {
      return this.dataset.giftWrapKey || '';
    }

    get context() {
      return this.dataset.context || 'drawer';
    }

    setBusy(isBusy) {
      this.toggleAttribute('busy', isBusy);
    }

    showError(message) {
      const element = this.querySelector('[data-gift-wrap-error]');
      if (!element) return;
      element.textContent = message || '';
      element.hidden = !message;
    }
  }

  /* ----------------------------------------------------- the per-line tick box */

  class GiftWrapLine extends GiftWrapBase {
    connectedCallback() {
      // A hidden marker on a line that may no longer be wrapped but still carries the property.
      if (this.hasAttribute('data-gift-wrap-strip')) {
        this.stripExcluded();
        return;
      }

      this.checkbox = this.querySelector('[data-gift-wrap-checkbox]');
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      if (this.checkbox) {
        this.checkbox.addEventListener('change', this.onToggle);
        this.checkbox.addEventListener('keydown', this.onKeydown);
      }
    }

    disconnectedCallback() {
      // The strip variant never binds anything, so guard on the handlers as well as the element.
      if (this.checkbox && this.onToggle) {
        this.checkbox.removeEventListener('change', this.onToggle);
        this.checkbox.removeEventListener('keydown', this.onKeydown);
      }
    }

    /**
     * Silently un-wrap a line whose product has since been marked as non-wrappable. Without this
     * the customer keeps paying a wrapping fee for an item that no longer offers a tick box — a
     * charge they can see in the total but have no way to cancel.
     *
     * Runs once: after the property is cleared the Liquid stops emitting the marker, so the
     * re-render cannot loop.
     */
    stripExcluded() {
      serial(async () => {
        try {
          const cart = await getCart();
          const line = cart.items.find((item) => item.key === this.lineKey);
          if (!line) return;

          if (!hasWrapKey(line, this.dataset.propertyKey)) return;

          // Drop the key, never blank it (see unwrapLine and the note at the top, STO-996).
          await unwrapLine(line, this.dataset.propertyKey);

          await reconcile();
        } catch (error) {
          // Best effort — the next page load tries again. If the line was removed but could not
          // be added back, still take the fee away with it.
          await reconcile().catch(() => {});
          return;
        }

        refreshCart(this.context);
      });
    }

    /**
     * Enter on a focused checkbox triggers implicit form submission, and in the drawer the first
     * submit button in this form is Checkout. Without this, ticking a box by keyboard and pressing
     * Enter sends the customer straight to payment.
     */
    onKeydown(event) {
      if (event.key === 'Enter') event.preventDefault();
    }

    onToggle(event) {
      // Belt and braces alongside the attribute rename: <line-item> listens for bubbling `change`.
      event.stopPropagation();

      const shouldWrap = this.checkbox.checked;

      this.showError('');
      this.setBusy(true);
      this.checkbox.disabled = true;

      serial(async () => {
        try {
          const cart = await getCart();
          let line = cart.items.find((item) => item.key === this.lineKey);

          if (!line) {
            // The key went stale — something else changed the cart under us (another tab, or a
            // property write that reminted it). Fall back to the variant, preferring a line whose
            // wrapped state still disagrees with what was just clicked.
            // Duplicate lines of one variant are possible here (a wrapped line never merges with
            // an unwrapped one, because their properties differ), so match on quantity and
            // current wrapped state too. If that still leaves it ambiguous, refuse — ticking the
            // wrong row would charge for the wrong number of wraps.
            const candidates = cart.items.filter(
              (item) =>
                !isFeeLine(item) &&
                String(item.variant_id) === String(this.dataset.variantId) &&
                String(item.quantity) === String(this.dataset.quantity) &&
                isWrapped(item) !== shouldWrap
            );

            line = candidates.length === 1 ? candidates[0] : null;
          }

          if (!line) {
            throw new Error(this.dataset.missingText || 'That item is no longer in your cart.');
          }

          if (shouldWrap) {
            const properties = Object.assign({}, line.properties || {});
            properties[this.dataset.propertyKey] = this.dataset.propertyValue;

            await postJson(`${ROOT}cart/change.js`, {
              id: line.key,
              quantity: line.quantity,
              properties
            });
          } else {
            // Drop the key, never blank it (see unwrapLine and the note at the top, STO-996).
            await unwrapLine(line, this.dataset.propertyKey);
          }

          await reconcile();
        } catch (error) {
          // If unwrapLine() removed the line but could not add it back, the fee must not outlive
          // it. Best effort; the next page load reconciles again either way.
          await reconcile().catch(() => {});

          // Never flip the checkbox back to guess the outcome — the property write may well have
          // landed before the failure. Show what went wrong and let the next render state the truth.
          this.showError(error.message);
          this.setBusy(false);
          this.checkbox.disabled = false;
          return;
        }

        refreshCart(this.context);
      });
    }
  }

  /* ------------------------------------------------------------------ wiring up */

  if (!customElements.get('gift-wrap-line')) customElements.define('gift-wrap-line', GiftWrapLine);

  const clearReloadGuard = () => {
    try {
      sessionStorage.removeItem(RELOAD_GUARD);
    } catch (error) {
      /* ignore */
    }
  };

  /**
   * Keep the fee quantity honest after changes we did not make — most importantly the theme's own
   * quantity stepper on a line that is ticked for wrapping.
   *
   * `cart:change` is the theme's post-mutation event; `cart:refresh` (what we dispatch) does not
   * re-fire it, so this cannot loop. On /cart the theme reloads rather than re-rendering, so the
   * page-load pass reloads at most once per visit-cycle.
   */
  const syncAfterExternalChange = (allowReload) =>
    serial(async () => {
      let changed = false;

      try {
        // Lines left blank by the old untick are cleaned first (STO-996).
        const cleanup = await unwrapBlankLines();
        const reconciled = await reconcile(cleanup.cart);
        changed = cleanup.changed || reconciled;
      } catch (error) {
        // Best-effort. The next explicit tick retries. A clean-up that removed a line but could
        // not add it back must still take the fee away with it.
        await reconcile().catch(() => {});
        return;
      }

      if (!changed) {
        clearReloadGuard();
        return;
      }

      if (!onCartPage()) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      if (!allowReload) return;

      let alreadyReloaded = false;
      try {
        alreadyReloaded = Boolean(sessionStorage.getItem(RELOAD_GUARD));
        sessionStorage.setItem(RELOAD_GUARD, '1');
      } catch (error) {
        /* ignore */
      }

      if (alreadyReloaded) {
        // Already had one go at this and the cart still will not settle — stop reloading, and
        // release the guard so a later, genuinely fixable mismatch is not locked out.
        clearReloadGuard();
        return;
      }

      window.location.reload();
    });

  document.documentElement.addEventListener('cart:change', () => syncAfterExternalChange(false));

  /**
   * The theme re-renders the drawer with `replaceChildren`, which throws away the button we just
   * disabled and paints a fresh enabled one — precisely during the stepper-triggered reconcile the
   * lock exists for. Re-apply after every render. setPending(0) leaves the counter alone and just
   * re-runs the query.
   */
  ['cart-drawer:refreshed', 'cart:change', 'cart:refresh'].forEach((name) => {
    document.addEventListener(name, () => {
      setTimeout(() => setPending(0), 0);

      // Two passes: the drawer swap is synchronous, but `cart:refresh` triggers an async fetch in
      // the theme that rewrites the count a moment later.
      setTimeout(applyVisibleCartCount, 0);
      setTimeout(applyVisibleCartCount, 500);
    });
  });

  const syncOnLoad = () => {
    // `feeInCart` matters because the fee line renders no tick box — a cart left holding nothing
    // but the hidden fee would otherwise never get reconciled, leaving an invisible charge.
    if (!document.querySelector('gift-wrap-line') && !CONFIG.feeInCart) {
      clearReloadGuard();
      return;
    }

    // No .then(clearReloadGuard) here: reload() does not halt JS, so the microtask would erase
    // the guard before the page unloads and every pass would reload again — an infinite loop.
    syncAfterExternalChange(true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncOnLoad, { once: true });
  } else {
    syncOnLoad();
  }
})();
